import React, { useEffect, useRef, useState } from 'react'
import { Alert, Box, Button, Snackbar, Stack, Typography } from '@mui/material'
import { useAuth0 } from '@auth0/auth0-react'

import { DataTable } from '@/components/UI/DataTable'
import SkeletonRow from '@/components/UI/Skeleton'
import { PlatformPermission } from '@/domain/permissions/rbac'
import { usePermissions } from '@/domain/permissions/usePermissions'
import { ErrorPage } from '@/pages/Error'
import { useAppStore } from '@/store'
import { IManualIncome } from '@/types'
import { GENERIC_ERROR_MESSAGE } from '@/utils/constants'
import { actionButtonSx, secondaryButtonSx } from '../buttonStyles'
import { EmptyContainer, EmptyMessage } from '../EmptyState'
import { manualPayCycleColumns } from './columns'
import { ReviewManualPayCycleDialog } from './ReviewManualPayCycleDialog'
import { ManualPayCycleDecision } from './status'
import { useManualPayCycle } from './useManualPayCycle'

const errorMessage = (error: unknown) =>
  error instanceof Error && error.message ? error.message : GENERIC_ERROR_MESSAGE

interface ManualPayCycleProps {
  // Opens the page's detect income dialog, offered as the next step after an approval.
  onDetectIncome: () => void
  detectionBusy: boolean
}

export const ManualPayCycle: React.FC<ManualPayCycleProps> = ({
  onDetectIncome,
  detectionBusy,
}) => {
  // The customer in this tab's header, not the IndexedDB record every tab shares.
  const { user: customer } = useAppStore()
  const { data: income, isLoading, isError, review } = useManualPayCycle(customer?.id)
  const { user } = useAuth0()
  const { hasPermission } = usePermissions(user?.['https://carringtonlabs.io/appRole'])
  const canReview = hasPermission(PlatformPermission.UpdateUserDetails)

  const [decision, setDecision] = useState<ManualPayCycleDecision | null>(null)
  // Captured when the dialog opens, so the review only ever goes to the customer whose request the
  // dialog describes.
  const [reviewUserId, setReviewUserId] = useState<string | null>(null)
  // An approval stays in the dialog as a next step: FHS has to re-detect income before the
  // approved pay cycle is reflected in the primary income.
  const [approved, setApproved] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string>()
  const [toast, setToast] = useState<string>()
  // Bumped whenever the dialog is closed, so a review still in flight from a dialog that has since
  // been dropped (the customer changed) cannot write its outcome into whatever dialog is open now.
  const dialogGeneration = useRef(0)

  const openDialog = (next: ManualPayCycleDecision) => {
    if (!customer?.id) return
    setError(undefined)
    setReviewUserId(customer.id)
    setDecision(next)
  }

  const closeDialog = () => {
    dialogGeneration.current += 1
    setIsSaving(false)
    setDecision(null)
    setReviewUserId(null)
    setApproved(false)
    setError(undefined)
  }

  // A review open for one customer must not carry over to the next: the dialog would show the new
  // customer's request while still holding the old customer's id.
  useEffect(() => {
    if (reviewUserId && reviewUserId !== customer?.id) closeDialog()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer?.id])

  const openDetectIncome = () => {
    closeDialog()
    onDetectIncome()
  }

  const confirm = async () => {
    if (!decision || !reviewUserId) return
    const generation = dialogGeneration.current
    const isStale = () => generation !== dialogGeneration.current
    setIsSaving(true)
    setError(undefined)
    try {
      await review(decision, reviewUserId)
      // The review still landed for the customer it was sent for; only this dialog's view of it is
      // dropped.
      if (isStale()) return
      if (decision === 'approve') {
        setApproved(true)
      } else {
        setToast('Manual pay cycle rejected.')
        setDecision(null)
        setReviewUserId(null)
      }
    } catch (e) {
      if (!isStale()) setError(errorMessage(e))
    } finally {
      if (!isStale()) setIsSaving(false)
    }
  }

  // Only a pending request can be reviewed; once decided it is a record, not a task.
  const canAct = canReview && income?.status === 'PENDING'

  const Header = (
    <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} mb={2}>
      <Typography variant="h6">Manual Pay Cycle</Typography>
      {canAct && (
        <Stack direction="row" gap={1}>
          <Button variant="outlined" onClick={() => openDialog('reject')} sx={secondaryButtonSx}>
            Reject
          </Button>
          <Button variant="contained" onClick={() => openDialog('approve')} sx={actionButtonSx}>
            Approve
          </Button>
        </Stack>
      )}
    </Box>
  )

  if (isLoading) {
    return (
      <>
        {Header}
        <SkeletonRow columns={manualPayCycleColumns} />
      </>
    )
  }

  if (isError) {
    return (
      <>
        {Header}
        <ErrorPage
          title="Unable to Fetch Manual Pay Cycle"
          message="We couldn't retrieve the manual pay cycle at the moment. Please check your internet connection or try again later. If the issue persists, contact administrator."
        />
      </>
    )
  }

  if (!income) {
    return (
      <>
        {Header}
        <EmptyContainer>
          <EmptyMessage>This customer hasn't submitted a manual pay cycle.</EmptyMessage>
        </EmptyContainer>
      </>
    )
  }

  return (
    <>
      {Header}
      <DataTable<IManualIncome> rows={[income]} columns={manualPayCycleColumns} />

      <ReviewManualPayCycleDialog
        decision={decision}
        approved={approved}
        manualIncome={income}
        isSaving={isSaving}
        error={error}
        onCancel={closeDialog}
        onConfirm={confirm}
        onDetectIncome={openDetectIncome}
        detectionBusy={detectionBusy}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(undefined)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="success" onClose={() => setToast(undefined)} sx={{ width: '100%' }}>
          {toast}
        </Alert>
      </Snackbar>
    </>
  )
}
